import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler,
    LineController,
    BarController
} from "chart.js";
import { Chart } from "react-chartjs-2";

import { FaArrowUp, FaArrowDown } from "react-icons/fa6";
import { FaLocationArrow } from "react-icons/fa";
import { FaChartSimple } from "react-icons/fa6";
import { FaCartArrowDown } from "react-icons/fa";
import { FaTriangleExclamation } from "react-icons/fa6";
import { FaBullhorn } from "react-icons/fa";
import { FaDropbox } from "react-icons/fa";
import { FaLayerGroup } from "react-icons/fa";
import { FaCalendarDays, FaChartLine, FaChartBar, FaArrowRightFromBracket, FaArrowRightToBracket } from "react-icons/fa6";

ChartJS.register(
    CategoryScale, LinearScale, BarElement,
    LineElement, PointElement, ArcElement,
    Tooltip, Legend, Filler, LineController,
    BarController
);

const API_URL = process.env.REACT_APP_API_BASE;

// ─── Helpers ────────────────────────────────────────────────────────────────
function decodeToken(token) {
    if (!token) return {};
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(c =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        return {};
    }
}

function getUserIdFromToken() {
    if (typeof window !== 'undefined' && typeof window.locatorgarsotrga === 'function') {
        const rawToken = window.locatorgarsotrga("token");
        if (rawToken) {
            const decoded = decodeToken(rawToken);
            return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
        }
    }
    const rawToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (rawToken) {
        const decoded = decodeToken(rawToken);
        return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
    }
    return { userId: null, token: null };
}

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) {
    const d = new Date(); d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
function formatChartDateRange(from, to) {
    const opts = { year: 'numeric', month: 'short', day: '2-digit' };
    const f = new Date(from); const t = new Date(to);
    if (isNaN(f.getTime()) || isNaN(t.getTime())) return '';
    return `${f.toLocaleDateString(undefined, opts)} - ${t.toLocaleDateString(undefined, opts)}`;
}

// ─── Direction helpers ───────────────────────────────────────────────────────
/**
 * OUTBOUND: message sent by the business TO a customer
 *   → from_number is null, to_number has a value
 * INBOUND: message received FROM a customer
 *   → from_number has a value, to_number is null
 */
function isOutbound(msg) {
    return !msg.from_number && !!msg.to_number;
}
function isInbound(msg) {
    return !!msg.from_number && !msg.to_number;
}

// ─── Status normalisation ────────────────────────────────────────────────────
// WhatsApp statuses we track for outbound messages
const OUTBOUND_STATUSES = ['sent', 'delivered', 'read', 'failed'];

function normaliseStatus(msg) {
    return (msg.status || msg.message_status || msg.delivery_status || '').toLowerCase();
}

// ─── Date filtering ──────────────────────────────────────────────────────────
function msgDate(msg) {
    return (msg.created_at || msg.received_at || '').slice(0, 10);
}
function inRange(msg, from, to) {
    const d = msgDate(msg);
    return d >= from && d <= to;
}

// ─── Daily bucketing ─────────────────────────────────────────────────────────
function buildDailySeries(messages, from, to) {
    // Create a map of date → counters for outbound messages only
    const map = {};
    let cursor = new Date(from);
    const end = new Date(to);
    while (cursor <= end) {
        const key = cursor.toISOString().slice(0, 10);
        map[key] = { date: key, sent: 0, delivered: 0, read: 0, failed: 0 };
        cursor.setDate(cursor.getDate() + 1);
    }

    messages.forEach(msg => {
        if (!isOutbound(msg)) return; // daily chart tracks outbound only
        const d = msgDate(msg);
        if (!map[d]) return;
        const st = normaliseStatus(msg);

        // Every outbound message counts as "sent"
        map[d].sent += 1;

        if (st === 'delivered') map[d].delivered += 1;
        else if (st === 'read') {
            // "read" implies delivered — count in both
            map[d].delivered += 1;
            map[d].read += 1;
        } else if (st === 'failed') {
            map[d].failed += 1;
        }
    });

    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

// ─── Category detection ───────────────────────────────────────────────────────
function detectCategory(msg) {
    // Use whatever category/template_type field exists on the message
    const cat = (msg.category || msg.template_type || msg.message_category || '').toLowerCase();
    if (cat.includes('market')) return 'marketing';
    if (cat.includes('util'))   return 'utility';
    if (cat.includes('auth'))   return 'authentication';
    return 'marketing'; // default outbound to marketing if unknown
}

// ─── Totals calculator ────────────────────────────────────────────────────────
/**
 * Returns totals computed purely from the messages array for the given date range.
 *
 * Stat logic:
 *   - total_sent      : all OUTBOUND messages in range (sent/delivered/read/failed all count as "sent")
 *   - total_delivered : outbound where status = 'delivered' OR 'read' (read implies delivered)
 *   - total_read      : outbound where status = 'read'
 *   - total_failed    : outbound where status = 'failed'
 *   - total_inbound   : inbound messages (status = 'received')
 *
 * Category totals (only outbound messages):
 *   - total / delivered per category
 */
function computeStats(allMessages, from, to) {
    const msgs = allMessages.filter(m => inRange(m, from, to));

    let total_sent = 0, total_delivered = 0, total_read = 0, total_failed = 0, total_inbound = 0;
    const catMap = {};

    msgs.forEach(msg => {
        const st = normaliseStatus(msg);

        if (isInbound(msg)) {
            total_inbound += 1;
            return; // don't count inbound in delivery stats
        }

        if (!isOutbound(msg)) return; // skip messages that are neither clearly in/outbound

        total_sent += 1;

        if (st === 'delivered') {
            total_delivered += 1;
        } else if (st === 'read') {
            total_delivered += 1; // read implies delivered
            total_read += 1;
        } else if (st === 'failed') {
            total_failed += 1;
        }

        // Category breakdown
        const cat = detectCategory(msg);
        if (!catMap[cat]) catMap[cat] = { total: 0, delivered: 0 };
        catMap[cat].total += 1;
        if (st === 'delivered' || st === 'read') catMap[cat].delivered += 1;
    });

    const categories = Object.entries(catMap).map(([category, v]) => ({
        category,
        total: v.total,
        delivered: v.delivered,
    }));

    const daily = buildDailySeries(msgs, from, to);

    return {
        totals: { total_sent, total_delivered, total_read, total_failed, total_inbound },
        categories,
        daily,
    };
}

// ─── Predefined date ranges ──────────────────────────────────────────────────
const predefinedRanges = [
    { label: "Today",       range: () => { const d = today(); return [d, d]; } },
    { label: "Yesterday",   range: () => { const d = daysAgo(1); return [d, d]; } },
    { label: "Last 7 Days", range: () => [daysAgo(6), today()] },
    { label: "Last 30 Days",range: () => [daysAgo(29), today()] },
    {
        label: "This Month",
        range: () => {
            const now = new Date();
            return [new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10), today()];
        }
    },
    {
        label: "Last Month",
        range: () => {
            const now = new Date();
            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            return [
                new Date(prev.getFullYear(), prev.getMonth(), 1).toISOString().slice(0, 10),
                new Date(prev.getFullYear(), prev.getMonth() + 1, 0).toISOString().slice(0, 10),
            ];
        }
    },
    { label: "Custom Range", range: null },
];

// ─── StatCard ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, value, label, trend, loading, accentColor }) {
    const color = accentColor || '#ed7b00';
    return (
        <div className='w-full sm:w-[48%] md:w-[32%] lg:w-[24%] border-0 rounded-[20px]
            shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]
            bg-[#fffffff2] cursor-pointer flex flex-col justify-between items-center p-5
            group hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)]
            relative hover:translate-y-[-10px] transition-all ease-in-out
            dark:bg-[#121519] dark:shadow-[0_4px_6px_-1px_rgba(255,255,255,0.05)]'>

            <div className='absolute top-0 left-1/2 w-full h-[2px]
                bg-[linear-gradient(90deg,_transparent,_#16D39A,_transparent)]
                scale-x-0 -translate-x-1/2 origin-center transition-all duration-300
                ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-x-100' />

            <div className='flex w-full justify-between'>
                <div>
                    {loading ? (
                        <div className='h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1' />
                    ) : (
                        <h1 className="text-[24px] font-semibold" style={{ color }}>{value ?? 0}</h1>
                    )}
                    <span className="text-[#6d6e70ba] uppercase text-[12px]">{label}</span>

                    {trend !== null && (
                        <p className='flex items-center gap-1 mt-1'>
                            <span className={`flex items-center text-[12px] gap-1 font-semibold ${trend >= 0 ? 'text-[#48bb78]' : 'text-red-400'}`}>
                                {trend >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                                {Math.abs(trend).toFixed(1)}%
                            </span>
                            <span className='text-[#ed7b00] text-[12px] font-semibold'>Since yesterday</span>
                        </p>
                    )}
                </div>

                <div className='w-[56px] h-[56px] rounded-[31%] flex items-center justify-center
                    text-[#fff] text-[24px] shadow-[0_4px_12px_rgba(22,211,154,0.3)]
                    transition-transform duration-300 group-hover:rotate-[360deg]'
                    style={{ backgroundColor: color }}>
                    {Icon && <Icon />}
                </div>
            </div>

            <div className='h-[3px] w-full bg-gradient-to-r from-[#16D39A] to-[#10B981]
                transition-[width] duration-500 rounded-[2px] opacity-0 group-hover:opacity-100 mt-[5px]' />
        </div>
    );
}

// ─── CategoryCard ─────────────────────────────────────────────────────────────
function CategoryCard({ icon: Icon, label, total, delivered, loading }) {
    const pct = total > 0 ? Math.round((delivered / total) * 100) : 0;
    return (
        <div className='w-full sm:w-[48%] lg:w-[32%] border-0 rounded-[20px]
            shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]
            bg-[#fffffff2] cursor-pointer flex flex-col justify-between items-center
            pl-5 pr-5 pb-5 pt-0 group
            hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)]
            relative hover:translate-y-[-10px] transition-all ease-in-out
            dark:bg-[#121519]'>

            <div className='absolute top-0 left-1/2 w-full h-[2px]
                bg-[linear-gradient(90deg,_transparent,_#16D39A,_transparent)]
                scale-x-0 -translate-x-1/2 origin-center transition-all duration-300
                ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-x-100' />

            <div className='w-full flex flex-col justify-center items-center pt-4 pb-2'>
                <div className='w-[56px] h-[56px] rounded-[31%] flex items-center justify-center
                    bg-[#ed7b00] shadow-[0_4px_12px_rgba(22,211,154,0.3)] text-[#fff] text-[24px]
                    transition-transform duration-300 group-hover:rotate-[360deg]'>
                    {Icon && <Icon />}
                </div>
                <span className="text-[#6d6e70ba] uppercase text-[12px] mt-2 dark:text-[#fff]">{label}</span>
            </div>

            <div className='flex items-center justify-between w-full'>
                <div>
                    {loading
                        ? <div className='h-6 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse' />
                        : <p className='text-[#ed7b00] text-[20px] font-semibold'>{total}</p>}
                    <p className='text-[#6d6e70ba] uppercase text-[12px] dark:text-[#fff]'>Total</p>
                </div>
                <div className='h-[40px] bg-[#48bb78] w-[2px]' />
                <div>
                    {loading
                        ? <div className='h-6 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse' />
                        : <p className='text-[#ed7b00] text-[20px] font-semibold'>{delivered}</p>}
                    <p className='text-[#6d6e70ba] uppercase text-[12px] dark:text-[#fff]'>Delivered</p>
                </div>
            </div>

            <div className='w-full mt-3'>
                <div className='flex justify-between text-[11px] text-[#6d6e70ba] mb-1'>
                    <span>Delivery rate</span>
                    <span className='text-[#ed7b00] font-semibold'>{pct}%</span>
                </div>
                <div className='h-[4px] w-full bg-[#e2e8f0] dark:bg-[#2d3748] rounded-full'>
                    <div className='h-full bg-gradient-to-r from-[#16D39A] to-[#10B981] rounded-full transition-all duration-700'
                        style={{ width: `${pct}%` }} />
                </div>
            </div>
        </div>
    );
}

// ─── DateRangeDropdown ────────────────────────────────────────────────────────
function DateRangeDropdown({ show, onClose, onPick, activeKey }) {
    const ref = useRef();
    useEffect(() => {
        if (!show) return;
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [show, onClose]);
    if (!show) return null;
    return (
        <div ref={ref} className="absolute z-30 mt-2 left-0 bg-white dark:bg-[#23272d] rounded-xl shadow-lg p-2 w-[240px]">
            <ul className="flex flex-col gap-1">
                {predefinedRanges.map(rng => (
                    <li key={rng.label}
                        className={`rounded px-3 py-[7px] text-[14px] cursor-pointer font-medium transition-colors
                            ${activeKey === rng.label
                                ? 'bg-[#16D39A] text-white'
                                : 'hover:bg-[#f6faf7] dark:hover:bg-[#2d3748] text-[#23272d] dark:text-[#d1d5db]'}`}
                        onClick={e => { e.stopPropagation(); onPick(rng.label); }}>
                        {rng.label}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard() {
    // All messages fetched once (unfiltered by date)
    const [allMessages, setAllMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date range state
    const [fromDate, setFromDate] = useState(daysAgo(6));
    const [toDate, setToDate] = useState(today());
    const [rangeDropdownOpen, setRangeDropdownOpen] = useState(false);
    const [activeRange, setActiveRange] = useState("Last 7 Days");
    const [customFrom, setCustomFrom] = useState(daysAgo(6));
    const [customTo, setCustomTo] = useState(today());
    const [showCustomModal, setShowCustomModal] = useState(false);

    const [chartType, setChartType] = useState("bar");

    const { userId, token } = getUserIdFromToken();

    const [wabaIds, setWabaIds] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [whatsappcampagin, setwhatsappcampagin] = useState([]);
    const [templateTypeCounts, setTemplateTypeCounts] = useState({
        marketing: 0,
        authentication: 0,
        utility: 0
    });

    useEffect(() => {
        if (!userId) {
            setWabaIds([]);
            return;
        }
        const fetchWabaIds = async () => {
            try {
                const res = await fetch(
                    `${API_URL}/auth/whatsapp/waba_ids?user_id=${encodeURIComponent(userId)}`,
                    { credentials: 'include', headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (Array.isArray(data.waba_ids)) {
                    setWabaIds(data.waba_ids);
                } else {
                    setWabaIds([]);
                }
            } catch (err) {
                console.error("Failed to fetch WABA IDs:", err);
                setWabaIds([]);
            }
        };
        fetchWabaIds();
    }, [userId, token]);

    useEffect(() => {
        if (!wabaIds || wabaIds.length === 0) {
            setTemplates([]);
            return;
        }
        // Choose the first wabaId for simplicity (can be adapted for multi-selection)
        const wabaId = wabaIds[0];

        // Fetch templates for the selected wabaId from the backend
        const fetchTemplates = async () => {
            try {
                const res = await fetch(
                    `${API_URL}/auth/template/by-waba/${encodeURIComponent(wabaId)}`,
                    {
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const data = await res.json();
                if (data.success && Array.isArray(data.data)) {
                    setTemplates(data.data);
                } else {
                    setTemplates([]);
                }
            } catch (err) {
                console.error("Error fetching templates by wabaId:", err);
                setTemplates([]);
            }
        };

        fetchTemplates();
    }, [wabaIds, token]);

    useEffect(() => {
        // Fetch WhatsApp campaign cost summaries for this user
        const fetchCampaignCosts = async () => {
            try {
                const res = await fetch(
                    `${API_URL}/auth/campaigns/cost-summary?client_id=${encodeURIComponent(userId)}`,
                    {
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const data = await res.json();
                if (data && Array.isArray(data.data)) {
                    setwhatsappcampagin(data.data);
                } else if (Array.isArray(data)) {
                    setwhatsappcampagin(data);
                } else {
                    setwhatsappcampagin([]);
                }
            } catch (err) {
                console.error("Error fetching WhatsApp campaign cost summaries:", err);
                setwhatsappcampagin([]);
            }
        };

        // Only fetch if we have a userId and token
        if (userId && token) {
            fetchCampaignCosts();
        } else {
            setwhatsappcampagin([]);
        }
    }, [userId, token]);

    // Compute template category counts by mapping campaign template_name to templates' name and category
    useEffect(() => {
        // Map of template name (case-insensitive) to template object
        const templateMap = templates.reduce((acc, tpl) => {
            if (tpl.name) acc[tpl.name.toLowerCase()] = tpl;
            return acc;
        }, {});

        // Counters for each recognized category
        const counts = { marketing: 0, authentication: 0, utility: 0 };

        // Support whatsappcampagin as either {data: [...]}, or an array
        let campList = whatsappcampagin;
        if (whatsappcampagin && typeof whatsappcampagin === 'object' && Array.isArray(whatsappcampagin.data)) {
            campList = whatsappcampagin.data;
        }

        if (!Array.isArray(campList)) {
            setTemplateTypeCounts(counts);
            return;
        }

        campList.forEach((camp) => {
            if (!camp.template_name) return;
            // Do case-insensitive lookup in templateMap
            const tpl = templateMap[camp.template_name.toLowerCase()];
            if (!tpl || !tpl.category) return;
            let type = tpl.category.toLowerCase();
            // Standardize category mappings
            if (type === "auth") type = "authentication";
            if (type === "authentication" || type === "utility" || type === "util" || type === "marketing") {
                if (type === "util") type = "utility";
                if (counts[type] !== undefined) counts[type]++;
            }
        });

        setTemplateTypeCounts(counts);
    }, [whatsappcampagin, templates]);

    // ── Fetch ALL messages once ──────────────────────────────────────────────
    const fetchMessages = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            // Step 1: get phone_number_ids for this user
            const phoneRes = await fetch(
                `${API_URL}/auth/phone_number_id?userId=${encodeURIComponent(userId)}`,
                { credentials: 'include', headers: { Authorization: `Bearer ${token}` } }
            );
            const phoneData = await phoneRes.json();
            const phoneNumberIds = (phoneData.success && Array.isArray(phoneData.phone_number_ids))
                ? phoneData.phone_number_ids : [];

            if (!phoneNumberIds.length) { setAllMessages([]); setLoading(false); return; }

            // Step 2: fetch messages for each phone_number_id
            const msgArrays = await Promise.all(
                phoneNumberIds.map(async (pid) => {
                    try {
                        const res = await fetch(
                            `${API_URL}/auth/messages?phone_number_id=${encodeURIComponent(pid)}`,
                            { credentials: 'include', headers: { Authorization: `Bearer ${token}` } }
                        );
                        const data = await res.json();
                        return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                    } catch { return []; }
                })
            );

            setAllMessages(msgArrays.flat());
        } catch (e) {
            console.error(e);
            setError('Failed to load messages.');
        } finally {
            setLoading(false);
        }
    }, [userId, token]);

    useEffect(() => { fetchMessages(); }, [fetchMessages]);

    // ── Compute stats from messages filtered by date range ───────────────────
    const stats = React.useMemo(
        () => computeStats(allMessages, fromDate, toDate),
        [allMessages, fromDate, toDate]
    );

    const { totals, categories, daily } = stats;

    // ── Stat card values ─────────────────────────────────────────────────────
    const totalSent      = totals.total_sent;
    const totalDelivered = totals.total_delivered;
    const totalRead      = totals.total_read;
    const totalFailed    = totals.total_failed;
    const totalInbound   = totals.total_inbound;

    // Calculate delivery rate (delivered / sent), safe for zero division
    const deliveryRate = totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0;

    const statCards = [
        { icon: FaArrowRightFromBracket, value: totalSent,      label: 'Outbound Sent',  accentColor: '#ed7b00' },
        { icon: FaChartSimple,           value: totalDelivered,  label: 'Delivered',      accentColor: '#16D39A' },
        { icon: FaCartArrowDown,         value: totalRead,       label: 'Read',           accentColor: '#4f46e5' },
        { icon: FaTriangleExclamation,   value: totalFailed,     label: 'Failed',         accentColor: '#ef4444' },
        { icon: FaArrowRightToBracket,   value: totalInbound,    label: 'Inbound Received', accentColor: '#0ea5e9' },
    ];

    console.log("templateTypeCounts" ,templateTypeCounts)
    // ── Category cards ───────────────────────────────────────────────────────
    const getCat = (name) =>
        categories.find(c => c.category === name) ?? { total: 0, delivered: 0 };

    const marketing      = templateTypeCounts.marketing;
    const utility        = templateTypeCounts.utility;
    const authentication = templateTypeCounts.authentication;

    // ── Chart ────────────────────────────────────────────────────────────────
    const chartLabels = daily.map(d => d.date);

    const chartData = {
        labels: chartLabels.length ? chartLabels : ['No data'],
        datasets: [
            {
                type: chartType === "bar" ? "line" : chartType,
                label: 'Sent',
                data: daily.map(d => d.sent),
                borderColor: '#f59e0b',
                backgroundColor: 'rgba(245,158,11,0.08)',
                borderWidth: 2, fill: false, tension: 0.4, pointRadius: 4, pointHoverRadius: 6,
            },
            {
                type: chartType === "bar" ? "line" : chartType,
                label: 'Delivered',
                data: daily.map(d => d.delivered),
                borderColor: '#16D39A',
                backgroundColor: 'rgba(22,211,154,0.08)',
                borderWidth: 2, fill: false, tension: 0.4, pointRadius: 4, pointHoverRadius: 6,
            },
            {
                type: chartType === "bar" ? "line" : chartType,
                label: 'Read',
                data: daily.map(d => d.read),
                borderColor: '#4f46e5',
                backgroundColor: 'rgba(79,70,229,0.08)',
                borderWidth: 2, fill: false, tension: 0.4, pointRadius: 4, pointHoverRadius: 6,
            },
            {
                type: chartType,
                label: 'Failed',
                data: daily.map(d => d.failed),
                backgroundColor: 'rgba(239,68,68,0.25)',
                borderColor: '#ef4444',
                borderWidth: 1, borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, padding: 16 } },
            tooltip: { cornerRadius: 8 },
        },
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 2, color: '#94a3b8' }, grid: { color: 'rgba(0,0,0,0.05)' } },
            x: { grid: { display: false }, ticks: { color: '#94a3b8', maxRotation: 45 } },
        },
    };

    // ── Date range handlers ──────────────────────────────────────────────────
    function handleRangePick(label) {
        setActiveRange(label);
        if (label === "Custom Range") {
            setShowCustomModal(true);
            setRangeDropdownOpen(false);
        } else {
            const [from, to] = predefinedRanges.find(r => r.label === label).range();
            setFromDate(from); setToDate(to);
            setCustomFrom(from); setCustomTo(to);
            setShowCustomModal(false); setRangeDropdownOpen(false);
        }
    }

    function handleCustomApply() {
        setFromDate(customFrom); setToDate(customTo);
        setActiveRange("Custom Range");
        setShowCustomModal(false); setRangeDropdownOpen(false);
    }

    return (
        <div className='min-h-screen w-full flex flex-col gap-6 dark:bg-[#121519] pb-8'>

            {/* ── Controls Row ─────────────────────────────────────────── */}
            <div className="flex justify-between items-center flex-wrap gap-3 px-3 py-3 md:pt-3">
                <h6 className="pt-2 text-[18px] font-bold text-[#ed7b00]">Status Analytics</h6>

                <div className="relative flex items-center gap-1 mt-2">
                    {/* Date range picker */}
                    <div className="relative">
                        <button type="button"
                            className="px-3 py-2 rounded-lg bg-white border border-[#e2e8f0] dark:bg-[#1e2228] dark:border-[#2d3748] text-[#ed7b00] text-[15px] font-semibold shadow-sm flex items-center gap-2"
                            onClick={() => { setRangeDropdownOpen(true); setShowCustomModal(false); }}>
                            <FaCalendarDays />
                            <span>{formatChartDateRange(fromDate, toDate)}</span>
                        </button>

                        <DateRangeDropdown
                            show={rangeDropdownOpen && !showCustomModal}
                            onClose={() => setRangeDropdownOpen(false)}
                            onPick={handleRangePick}
                            activeKey={activeRange}
                        />

                        {showCustomModal && (
                            <div className="absolute mt-2 left-0 z-40 bg-white dark:bg-[#23272d] rounded-xl shadow-lg p-4 w-[270px]">
                                <div className="mb-2 text-[13px] text-[#6d6e70ba] font-medium">Custom Range</div>
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <label className="w-12 text-[13px]">From</label>
                                        <input type="date" value={customFrom} max={customTo}
                                            onChange={e => setCustomFrom(e.target.value)}
                                            className="px-2 py-1 rounded border border-[#e2e8f0] bg-white dark:bg-[#181c21] text-[#ed7b00] font-semibold" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="w-12 text-[13px]">To</label>
                                        <input type="date" value={customTo} min={customFrom} max={today()}
                                            onChange={e => setCustomTo(e.target.value)}
                                            className="px-2 py-1 rounded border border-[#e2e8f0] bg-white dark:bg-[#181c21] text-[#ed7b00] font-semibold" />
                                    </div>
                                    <div className="flex gap-2 justify-end mt-2">
                                        <button type="button"
                                            className="bg-[#f3f4f6] dark:bg-[#23272d] px-3 py-1 rounded font-semibold"
                                            onClick={() => setShowCustomModal(false)}>Cancel</button>
                                        <button type="button"
                                            className="bg-[#16D39A] text-white px-4 py-1 rounded font-semibold"
                                            onClick={handleCustomApply}>Apply</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Line chart toggle */}
                    <button type="button"
                        className={`ml-2 px-[13px] py-2 rounded-lg bg-white border border-[#e2e8f0] dark:bg-[#1e2228] dark:border-[#2d3748]
                            ${chartType === "line" ? "bg-[#16D39A] text-white" : "text-[#ed7b00] hover:bg-[#f5f7fa]"}`}
                        onClick={() => setChartType("line")}>
                        <FaChartLine />
                    </button>

                    {/* Bar chart toggle */}
                    <button type="button"
                        className={`ml-1 px-[13px] py-2 rounded-lg bg-white border border-[#e2e8f0] dark:bg-[#1e2228] dark:border-[#2d3748]
                            ${chartType === "bar" ? "bg-[#16D39A] text-white" : "text-[#ed7b00] hover:bg-[#f5f7fa]"}`}
                        onClick={() => setChartType("bar")}>
                        <FaChartBar />
                    </button>
                </div>
            </div>

            {/* ── Stat Cards (5 cards: 4 outbound + 1 inbound) ─────────── */}
            <div className='flex flex-wrap gap-y-5 justify-between items-center w-full sm:pl-6 pl-0'>
                {statCards.map((card, i) => (
                    <StatCard key={i} {...card} loading={loading} trend={null} />
                ))}
            </div>

            {/* ── Direction summary badge ───────────────────────────────── */}
            {!loading && (
                <div className='sm:pl-6 pl-1'>
                    <div className='flex flex-wrap gap-3'>
                        <span className='flex items-center gap-2 bg-[#ed7b001a] text-[#ed7b00] rounded-full px-4 py-1 text-[12px] font-semibold'>
                            <FaArrowRightFromBracket /> Outbound (sent by you): {totalSent}
                        </span>
                        <span className='flex items-center gap-2 bg-[#0ea5e91a] text-[#0ea5e9] rounded-full px-4 py-1 text-[12px] font-semibold'>
                            <FaArrowRightToBracket /> Inbound (received from contacts): {totalInbound}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Category Cards ────────────────────────────────────────── */}
            <div className='flex flex-wrap gap-y-5 justify-between items-center w-full sm:pl-6 pl-1'>
                <CategoryCard icon={FaBullhorn}  label='Marketing'      total={marketing}      delivered={marketing.delivered}      loading={loading} />
                <CategoryCard icon={FaDropbox}   label='Utility'        total={utility}        delivered={utility.delivered}        loading={loading} />
                <CategoryCard icon={FaLayerGroup} label='Authentication' total={authentication} delivered={authentication.delivered} loading={loading} />
            </div>

            {error && (
                <div className='text-red-400 text-[12px] font-medium text-right pr-6'>⚠ {error}</div>
            )}

            {/* ── Chart ─────────────────────────────────────────────────── */}
            <div className='sm:pl-6 pl-1'>
                <div className='w-full rounded-[20px] bg-[#fffffff2] dark:bg-[#1e2228] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] p-5'>
                    <div className='flex justify-between items-center mb-4'>
                        <h2 className='text-[#ed7b00] font-semibold text-[15px]'>Message Activity (Outbound)</h2>
                        {!loading && daily.length === 0 && (
                            <span className='text-[12px] text-[#6d6e70ba]'>No data for selected range</span>
                        )}
                    </div>
                    <div className='h-[350px]'>
                        {loading ? (
                            <div className='h-full flex items-center justify-center'>
                                <div className='w-8 h-8 border-4 border-[#16D39A] border-t-transparent rounded-full animate-spin' />
                            </div>
                        ) : (
                            <Chart type={chartType} data={chartData} options={chartOptions} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Delivery Overview ─────────────────────────────────────── */}
            {!loading && (
                <div className='sm:pl-6 pl-1'>
                    <div className='w-full rounded-[20px] bg-[#fffffff2] dark:bg-[#1e2228] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] p-5'>
                        <h2 className='text-[#ed7b00] font-semibold text-[15px] mb-4'>Delivery Overview</h2>
                        <div className="mb-3 flex items-center gap-4">
                            <span className="inline-block bg-[#16D39A1a] text-[#16D39A] px-4 py-[6px] rounded-full text-[13px] font-bold">
                                Delivery rate: {deliveryRate}%
                            </span>
                        </div>
                        <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
                            {[
                                { label: 'Sent',      value: totalSent,      color: '#f59e0b' },
                                { label: 'Delivered', value: totalDelivered, color: '#16D39A' },
                                { label: 'Read',      value: totalRead,      color: '#4f46e5' },
                                { label: 'Failed',    value: totalFailed,    color: '#ef4444' },
                            ].map(item => (
                                <div key={item.label} className='flex flex-col gap-1'>
                                    <div className='flex justify-between text-[12px]'>
                                        <span className='text-[#6d6e70ba] uppercase font-semibold'>{item.label}</span>
                                        <span style={{ color: item.color }} className='font-bold'>{item.value}</span>
                                    </div>
                                    <div className='h-[6px] bg-[#f1f5f9] dark:bg-[#2d3748] rounded-full overflow-hidden'>
                                        <div className='h-full rounded-full transition-all duration-700'
                                            style={{
                                                width: totalSent > 0 ? `${Math.round((item.value / totalSent) * 100)}%` : '0%',
                                                backgroundColor: item.color,
                                            }} />
                                    </div>
                                    <span className='text-[11px] text-[#94a3b8]'>
                                        {totalSent > 0 ? Math.round((item.value / totalSent) * 100) : 0}% of sent
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;